import Mustache from 'mustache';
import Modal from '@/js/plugins/modal';
import termsModalHtml from '@/js/html_templates/terms-modal.html';
import connectionModalHtml from '@/js/html_templates/connection-modal.html';
import userTerms20260208Html from '@/js/html_templates/user/terms/2026-02-08.html';
import adminTerms20260208Html from '@/js/html_templates/admin/terms/2026-02-08.html';
import { csrfToken } from '@/js/_common';
import { setSelectAction } from '@/js/_components/forms/input-select';

const CONNECTION_FIELDS = ['human_connection', 'local_connection', 'work_connection'];

const toOptionList = (map) => {
    if (!map || typeof map !== 'object') return [];
    return Object.entries(map).map(([value, label]) => ({ value, label }));
};

const parseConnectionOptions = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const buildConnectionTemplateData = ({ optionsMap }) => ({
    size: 'medium',
    human_options: toOptionList(optionsMap?.human_connection),
    local_options: toOptionList(optionsMap?.local_connection),
    work_options: toOptionList(optionsMap?.work_connection),
});

const submitJson = async (url, payload) => {
    return fetch(url, {
        method: 'POST',
        headers: {
            'X-CSRF-TOKEN': csrfToken,
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
    });
};

const userTermsMessageMap = {
    '2026-02-08': userTerms20260208Html,
};

const adminTermsMessageMap = {
    '2026-02-08': adminTerms20260208Html,
};

const getUserTermsMessageHtml = (termsDate) => {
    if (!termsDate) return '';

    const template = userTermsMessageMap[termsDate];
    return template?.default ?? template ?? '';
};

const getAdminTermsMessageHtml = (termsDate) => {
    if (!termsDate) return '';

    const template = adminTermsMessageMap[termsDate];
    return template?.default ?? template ?? '';
};

const setupTermsAgreementModal = ({
    acceptedVersion,
    currentVersion,
    termsDate,
    termsUrl,
    acceptUrl,
    messageHtml,
    onAccepted,
}) => {
    if (!currentVersion || acceptedVersion >= currentVersion || !acceptUrl) {
        return null;
    }

    const modal = new Modal({
        overlayClose: false,
        renderClose: false,
    });

    const html = Mustache.render(termsModalHtml, {
        terms_url: termsUrl,
        size: 'medium',
    });

    modal.setHtml(html);
    modal.show();

    const root = document.querySelector('.c-parts-terms-modal');
    if (!root) return modal;

    const messageArea = root.querySelector('.c-parts-terms-modal__message');
    const confirmButton = root.querySelector('[data-action="confirm"]');

    if (messageArea) {
        messageArea.innerHTML = messageHtml || '<p>利用規約が更新されました。内容をご確認のうえ、同意をお願いします。</p>';
    }

    if (confirmButton) {
        confirmButton.addEventListener('click', async () => {
            confirmButton.disabled = true;

            try {
                const response = await fetch(acceptUrl, {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({}),
                });

                if (!response.ok) {
                    throw new Error('failed to accept terms');
                }

                if (onAccepted) onAccepted(currentVersion);
                modal.close();
            } catch (e) {
                alert('同意処理に失敗しました。時間をおいて再度お試しください。');
                confirmButton.disabled = false;
            }
        });
    }

    return modal;
};

export const setupUserTermsAgreementModal = ({ onAfterAccept } = {}) => {
    const body = document.body;
    if (!body || body.dataset.login !== 'logged-in') return null;

    const envCurrentVersion = Number(process.env.MIX_USER_TERMS_VERSION || 0);
    const acceptedVersion = Number(body.dataset.userTermsVersion || 0);
    const currentVersion = Number(body.dataset.currentUserTermsVersion || envCurrentVersion || 0);
    const termsDate = body.dataset.currentUserTermsDate || '';
    const termsUrl = '/terms';
    const acceptUrl = '/mypage/terms/accept';
    const messageHtml = getUserTermsMessageHtml(termsDate);

    return setupTermsAgreementModal({
        acceptedVersion,
        currentVersion,
        termsDate,
        termsUrl,
        acceptUrl,
        messageHtml,
        onAccepted: (version) => {
            body.dataset.userTermsVersion = String(version);
            if (onAfterAccept) onAfterAccept(version);
        },
    });
};

const readConnectionPayload = (root) => {
    const payload = {};
    for (const field of CONNECTION_FIELDS) {
        const select = root.querySelector(`select[name="${field}"]`);
        payload[field] = select ? select.value : '';
    }
    return payload;
};

const initConnectionSelects = (root) => {
    root.querySelectorAll('.c-forms-input-select [data-select-block]').forEach((el) => {
        setSelectAction(el);
    });
};

const validateConnectionPayload = (payload) => {
    return CONNECTION_FIELDS.every((field) => payload[field]);
};

const setupConnectionModal = ({
    optionsMap,
    connectionUrl,
    onConnectionSaved,
}) => {
    if (!optionsMap || !connectionUrl) return null;

    const modal = new Modal({
        overlayClose: false,
        renderClose: false,
    });

    const html = Mustache.render(
        connectionModalHtml,
        buildConnectionTemplateData({ optionsMap }),
    );

    modal.setHtml(html);
    modal.show();

    const root = document.querySelector('.c-parts-connection-modal');
    if (!root) return modal;

    initConnectionSelects(root);

    const errorArea = root.querySelector('[data-error]');
    const confirmButton = root.querySelector('[data-action="confirm"]');
    if (!confirmButton) return modal;

    const showError = (msg) => {
        if (!errorArea) return;
        errorArea.textContent = msg;
        errorArea.hidden = false;
    };
    const clearError = () => {
        if (!errorArea) return;
        errorArea.textContent = '';
        errorArea.hidden = true;
    };

    confirmButton.addEventListener('click', async () => {
        clearError();
        const payload = readConnectionPayload(root);
        if (!validateConnectionPayload(payload)) {
            showError('全ての項目を選択してください。');
            return;
        }

        confirmButton.disabled = true;
        try {
            const connRes = await submitJson(connectionUrl, payload);
            if (!connRes.ok) throw new Error('failed to save connection');
            if (onConnectionSaved) onConnectionSaved(payload);

            modal.close();
        } catch (e) {
            showError('保存に失敗しました。時間をおいて再度お試しください。');
            confirmButton.disabled = false;
        }
    });

    return modal;
};

export const setupUserLoginRequiredModals = () => {
    const body = document.body;
    if (!body || body.dataset.login !== 'logged-in') return null;

    const envCurrentVersion = Number(process.env.MIX_USER_TERMS_VERSION || 0);
    const acceptedVersion = Number(body.dataset.userTermsVersion || 0);
    const currentVersion = Number(body.dataset.currentUserTermsVersion || envCurrentVersion || 0);
    const connectionUrl = '/mypage/profile/connection_update';
    const optionsMap = parseConnectionOptions(body.dataset.amamiConnectionOptions);

    const termsNeeded = !!currentVersion && acceptedVersion < currentVersion;
    const connectionNeeded = body.dataset.amamiConnectionNeeded === '1' && !!optionsMap;

    const showConnectionModal = () => setupConnectionModal({
        optionsMap,
        connectionUrl,
        onConnectionSaved: () => {
            body.dataset.amamiConnectionNeeded = '0';
        },
    });

    if (termsNeeded) {
        // 規約同意モーダル → 同意後にcloseアニメーション完了を待ってから次のフローへ
        return setupUserTermsAgreementModal({
            onAfterAccept: () => {
                // modal/styles.scssのopacity transition (0.4s) 完了後に次を表示
                setTimeout(() => {
                    // 誕生日未入力ユーザー向けの_profile-add-guideを表示
                    const guide = document.querySelector('.c-parts-profile-add-guide[data-pending-terms]');
                    if (guide) guide.removeAttribute('data-pending-terms');

                    if (connectionNeeded) showConnectionModal();
                }, 450);
            },
        });
    }

    if (connectionNeeded) {
        return showConnectionModal();
    }

    return null;
};

export const setupAdminTermsAgreementModal = () => {
    const body = document.body;
    if (!body || body.dataset.login !== 'logged-in') return null;

    const envCurrentVersion = Number(process.env.MIX_ADMIN_TERMS_VERSION || 0);
    const acceptedVersion = Number(body.dataset.adminTermsVersion || 0);
    const currentVersion = Number(body.dataset.currentAdminTermsVersion || envCurrentVersion || 0);
    const termsDate = body.dataset.currentAdminTermsDate || '';
    const termsUrl = '/terms/company';
    const acceptUrl = '/admin/terms/accept';
    const messageHtml = getAdminTermsMessageHtml(termsDate);

    return setupTermsAgreementModal({
        acceptedVersion,
        currentVersion,
        termsDate,
        termsUrl,
        acceptUrl,
        messageHtml,
        onAccepted: (version) => {
            body.dataset.adminTermsVersion = String(version);
        },
    });
};

export default setupUserTermsAgreementModal;
